import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MagnifyingGlass, DownloadSimple, Star, StarHalf, Upload, User, Tag,
  FileText, Bird, Plant, Tree, PawPrint, Bug, Flower, FirstAidKit,
  Footprints, Fish, Butterfly, Mountains, CaretLeft, Funnel, ArrowsDownUp,
  X, Check
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { API, templateIcons } from "@/config/constants";

const categoryOptions = [
  { value: "all", label: "All Categories" },
  { value: "general", label: "General" },
  { value: "birds", label: "Birds" },
  { value: "plants", label: "Plants" },
  { value: "wildlife", label: "Wildlife" },
  { value: "insects", label: "Insects" },
  { value: "marine", label: "Marine" },
  { value: "fungi", label: "Fungi" },
  { value: "hiking", label: "Hiking" },
  { value: "photography", label: "Photography" },
];

const iconOptions = [
  { value: "file", Icon: FileText },
  { value: "bird", Icon: Bird },
  { value: "plant", Icon: Plant },
  { value: "tree", Icon: Tree },
  { value: "paw", Icon: PawPrint },
  { value: "mountains", Icon: Mountains },
  { value: "bug", Icon: Bug },
  { value: "flower", Icon: Flower },
  { value: "fungi", Icon: FirstAidKit },
  { value: "snake", Icon: Footprints },
  { value: "fish", Icon: Fish },
  { value: "butterfly", Icon: Butterfly },
];

const StarRating = ({ rating, onRate, interactive = false, size = 16 }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate?.(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          className={`${interactive ? "cursor-pointer hover:scale-110" : "cursor-default"} transition-transform`}
          data-testid={`star-${star}`}
        >
          <Star
            size={size}
            weight={(hover || rating) >= star ? "fill" : "regular"}
            className={(hover || rating) >= star ? "text-[#D99559]" : "text-[#D2C2B3]"}
          />
        </button>
      ))}
    </div>
  );
};

const MarketplacePage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("popular");
  const [showPublish, setShowPublish] = useState(false);
  const [myTemplates, setMyTemplates] = useState([]);
  const [myPublished, setMyPublished] = useState([]);
  const [importing, setImporting] = useState(null);
  const [publishForm, setPublishForm] = useState({
    name: "", icon: "file", title: "", content: "", tags: [], category: "general"
  });
  const [publishTagInput, setPublishTagInput] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (category && category !== "all") params.append("category", category);
      params.append("sort", sort);
      const response = await axios.get(`${API}/marketplace/templates?${params}`);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to load marketplace");
    } finally {
      setLoading(false);
    }
  }, [search, category, sort]);

  const fetchMyData = useCallback(async () => {
    try {
      const [customRes, publishedRes] = await Promise.all([
        axios.get(`${API}/templates/custom`),
        axios.get(`${API}/marketplace/my-published`)
      ]);
      setMyTemplates(customRes.data);
      setMyPublished(publishedRes.data);
    } catch (error) {
      console.error("Failed to load user templates");
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  useEffect(() => { fetchMyData(); }, [fetchMyData]);

  const handleImport = async (templateId) => {
    setImporting(templateId);
    try {
      await axios.post(`${API}/marketplace/import/${templateId}`);
      toast.success("Template imported to your collection!");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to import template");
    } finally {
      setImporting(null);
    }
  };

  const handleRate = async (templateId, rating) => {
    try {
      const res = await axios.post(`${API}/marketplace/${templateId}/rate`, { rating });
      setTemplates(prev => prev.map(t =>
        t.id === templateId ? { ...t, rating: res.data.rating, rating_count: res.data.rating_count } : t
      ));
      if (selectedDetail?.id === templateId) {
        setSelectedDetail(prev => ({ ...prev, rating: res.data.rating, rating_count: res.data.rating_count }));
      }
      toast.success("Rating submitted!");
    } catch (error) {
      toast.error("Failed to rate template");
    }
  };

  const handlePublish = async () => {
    if (!publishForm.name || !publishForm.title) {
      toast.error("Name and title prefix are required");
      return;
    }
    setPublishing(true);
    try {
      await axios.post(`${API}/marketplace/publish`, publishForm);
      toast.success("Template published to marketplace!");
      setShowPublish(false);
      setPublishForm({ name: "", icon: "file", title: "", content: "", tags: [], category: "general" });
      fetchTemplates();
      fetchMyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  const handlePublishExisting = async (template) => {
    try {
      await axios.post(`${API}/marketplace/publish`, {
        template_id: template.id,
        category: "general"
      });
      toast.success(`"${template.name}" published to marketplace!`);
      fetchMyData();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish");
    }
  };

  const handleUnpublish = async (templateId) => {
    try {
      await axios.delete(`${API}/marketplace/${templateId}`);
      toast.success("Template unpublished");
      fetchMyData();
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to unpublish");
    }
  };

  const addPublishTag = () => {
    if (publishTagInput.trim() && !publishForm.tags.includes(publishTagInput.trim())) {
      setPublishForm(prev => ({ ...prev, tags: [...prev.tags, publishTagInput.trim()] }));
      setPublishTagInput("");
    }
  };

  const publishedNames = myPublished.map(p => p.name);

  return (
    <div className="max-w-7xl mx-auto page-content" data-testid="marketplace-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <Button variant="ghost" onClick={() => navigate("/stats")} className="text-white" data-testid="back-to-stats">
          <CaretLeft size={20} />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">Template Marketplace</h1>
          <p className="text-xs sm:text-base text-white/70 mt-1">Browse, share, and import journal templates</p>
        </div>
        <Dialog open={showPublish} onOpenChange={setShowPublish}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#C25A3C] hover:bg-[#A84B32] text-white text-xs sm:text-sm shrink-0" data-testid="publish-new-btn">
              <Upload size={16} className="mr-1" />Publish
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#D2C2B3] max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Publish a Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[#55624F] mb-2">Template Name *</label>
                <Input value={publishForm.name} onChange={(e) => setPublishForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Beach Combing Guide" className="bg-[#EAE7DE] border-[#D2C2B3]" data-testid="publish-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#55624F] mb-2">Icon</label>
                <div className="flex flex-wrap gap-2">
                  {iconOptions.map(({ value, Icon }) => (
                    <Button key={value} type="button" variant={publishForm.icon === value ? "default" : "outline"} size="sm"
                      onClick={() => setPublishForm(prev => ({ ...prev, icon: value }))}
                      className={publishForm.icon === value ? "bg-[#55624F]" : "border-[#D2C2B3]"}>
                      <Icon size={16} />
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#55624F] mb-2">Category</label>
                <Select value={publishForm.category} onValueChange={(v) => setPublishForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="bg-[#EAE7DE] border-[#D2C2B3]" data-testid="publish-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categoryOptions.filter(c => c.value !== "all").map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#55624F] mb-2">Entry Title Prefix *</label>
                <Input value={publishForm.title} onChange={(e) => setPublishForm(prev => ({ ...prev, title: e.target.value }))} placeholder="e.g., Beach Walk -" className="bg-[#EAE7DE] border-[#D2C2B3]" data-testid="publish-title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#55624F] mb-2">Content Template</label>
                <Textarea value={publishForm.content} onChange={(e) => setPublishForm(prev => ({ ...prev, content: e.target.value }))} placeholder="Location:\n\nFinds:\n\nNotes:" rows={5} className="bg-[#EAE7DE] border-[#D2C2B3]" data-testid="publish-content" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#55624F] mb-2">Tags</label>
                <div className="flex gap-2 mb-2">
                  <Input value={publishTagInput} onChange={(e) => setPublishTagInput(e.target.value)} placeholder="Add tag" onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addPublishTag())} className="flex-1 bg-[#EAE7DE] border-[#D2C2B3]" data-testid="publish-tag-input" />
                  <Button type="button" onClick={addPublishTag} variant="outline" className="border-[#D2C2B3]"><Tag size={16} /></Button>
                </div>
                {publishForm.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {publishForm.tags.map(tag => (
                      <Badge key={tag} className="bg-[#EAE7DE] text-[#2E332B] cursor-pointer" onClick={() => setPublishForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))}>
                        {tag} <X size={12} className="ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowPublish(false)} className="flex-1 border-[#D2C2B3]">Cancel</Button>
                <Button onClick={handlePublish} disabled={publishing} className="flex-1 bg-[#55624F] hover:bg-[#424D3D] text-white" data-testid="publish-submit">
                  {publishing ? "Publishing..." : "Publish to Marketplace"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* My Templates - Quick Publish */}
      {myTemplates.length > 0 && (
        <div className="mb-8 mt-6">
          <h2 className="font-heading text-lg font-semibold text-white mb-3">Share Your Templates</h2>
          <div className="flex flex-wrap gap-3">
            {myTemplates.map(template => {
              const IconComponent = templateIcons[template.icon] || FileText;
              const isPublished = publishedNames.includes(template.name);
              return (
                <div key={template.id} className="flex items-center gap-2 bg-white border border-[#D2C2B3] rounded-lg px-3 py-2">
                  <IconComponent size={18} weight="duotone" className="text-[#55624F]" />
                  <span className="text-sm font-medium text-[#2E332B]">{template.name}</span>
                  {isPublished ? (
                    <Badge className="bg-[#55624F]/10 text-[#55624F] text-xs flex items-center gap-1"><Check size={10} />Published</Badge>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => handlePublishExisting(template)} className="text-xs text-[#C25A3C] hover:bg-[#C25A3C]/10 h-6 px-2" data-testid={`quick-publish-${template.id}`}>
                      <Upload size={12} className="mr-1" />Share
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C8068]" />
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#EAE7DE] border-[#D2C2B3]"
            data-testid="marketplace-search"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-full sm:w-44 bg-[#EAE7DE] border-[#D2C2B3]" data-testid="marketplace-category-filter">
            <Funnel size={14} className="mr-1" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categoryOptions.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-full sm:w-40 bg-[#EAE7DE] border-[#D2C2B3]" data-testid="marketplace-sort">
            <ArrowsDownUp size={14} className="mr-1" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">Most Popular</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="rating">Highest Rated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Template Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="border-[#D2C2B3]"><CardContent className="p-5"><Skeleton className="h-6 w-3/4 mb-3 bg-[#EAE7DE]" /><Skeleton className="h-4 w-full mb-2 bg-[#EAE7DE]" /><Skeleton className="h-4 w-2/3 bg-[#EAE7DE]" /></CardContent></Card>
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16">
          <FileText size={56} weight="duotone" className="mx-auto mb-4 text-[#D2C2B3]" />
          <p className="font-handwritten text-2xl text-[#7C8068] mb-2">
            {search || category !== "all" ? "No templates match your search" : "No shared templates yet"}
          </p>
          <p className="text-[#7C8068] mb-6">Be the first to share a template with the community!</p>
          <Button onClick={() => setShowPublish(true)} className="bg-[#C25A3C] hover:bg-[#A84B32] text-white">
            <Upload size={18} className="mr-2" />Publish a Template
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {templates.map(template => {
            const IconComponent = templateIcons[template.icon] || FileText;
            const isOwn = myPublished.some(p => p.id === template.id);
            return (
              <Card key={template.id} className="bg-white border-[#D2C2B3] card-hover group" data-testid={`marketplace-card-${template.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2.5 bg-[#EAE7DE] rounded-lg shrink-0">
                      <IconComponent size={26} weight="duotone" className="text-[#55624F]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg font-semibold text-[#2E332B] truncate">{template.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-[#7C8068] flex items-center gap-1"><User size={10} />{template.author_name}</span>
                        <Badge className="bg-[#EAE7DE] text-[#55624F] text-xs capitalize">{template.category}</Badge>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-[#7C8068] line-clamp-2 mb-3 min-h-[2.5rem]">
                    {template.content || `Title prefix: ${template.title}`}
                  </p>

                  {template.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {template.tags.slice(0, 4).map(tag => (
                        <Badge key={tag} className="bg-[#EAE7DE] text-[#7C8068] text-xs">{tag}</Badge>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <StarRating rating={template.rating} size={14} />
                      <span className="text-xs text-[#7C8068]">({template.rating_count})</span>
                    </div>
                    <span className="text-xs text-[#7C8068] flex items-center gap-1">
                      <DownloadSimple size={12} />{template.downloads} imports
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedDetail(template)}
                      className="flex-1 border-[#D2C2B3] text-[#55624F] text-xs"
                      data-testid={`view-detail-${template.id}`}
                    >
                      Preview
                    </Button>
                    {isOwn ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnpublish(template.id)}
                        className="flex-1 border-[#C25A3C] text-[#C25A3C] text-xs hover:bg-[#C25A3C] hover:text-white"
                        data-testid={`unpublish-${template.id}`}
                      >
                        Unpublish
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleImport(template.id)}
                        disabled={importing === template.id}
                        className="flex-1 bg-[#55624F] hover:bg-[#424D3D] text-white text-xs"
                        data-testid={`import-${template.id}`}
                      >
                        {importing === template.id ? "Importing..." : <><DownloadSimple size={14} className="mr-1" />Import</>}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail/Preview Dialog */}
      <Dialog open={!!selectedDetail} onOpenChange={() => setSelectedDetail(null)}>
        <DialogContent className="bg-white border-[#D2C2B3] max-w-lg">
          {selectedDetail && (() => {
            const DetailIcon = templateIcons[selectedDetail.icon] || FileText;
            const isOwn = myPublished.some(p => p.id === selectedDetail.id);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl flex items-center gap-3">
                    <div className="p-2 bg-[#EAE7DE] rounded-lg"><DetailIcon size={24} weight="duotone" className="text-[#55624F]" /></div>
                    {selectedDetail.name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-3">
                  <div className="flex items-center gap-3 text-sm text-[#7C8068]">
                    <span className="flex items-center gap-1"><User size={14} />{selectedDetail.author_name}</span>
                    <Badge className="bg-[#EAE7DE] text-[#55624F] capitalize">{selectedDetail.category}</Badge>
                    <span className="flex items-center gap-1"><DownloadSimple size={14} />{selectedDetail.downloads}</span>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-[#55624F] mb-1">Title Prefix</p>
                    <p className="text-sm text-[#2E332B] bg-[#EAE7DE] px-3 py-2 rounded-lg">{selectedDetail.title}</p>
                  </div>

                  {selectedDetail.content && (
                    <div>
                      <p className="text-xs font-medium text-[#55624F] mb-1">Content Template</p>
                      <pre className="text-sm text-[#2E332B] bg-[#EAE7DE] px-3 py-2 rounded-lg whitespace-pre-wrap font-sans">{selectedDetail.content}</pre>
                    </div>
                  )}

                  {selectedDetail.tags?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-[#55624F] mb-1">Default Tags</p>
                      <div className="flex flex-wrap gap-1">{selectedDetail.tags.map(tag => (<Badge key={tag} className="bg-[#EAE7DE] text-[#7C8068]">{tag}</Badge>))}</div>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-medium text-[#55624F] mb-2">Rate this template</p>
                    <div className="flex items-center gap-3">
                      <StarRating rating={selectedDetail.rating} interactive onRate={(r) => handleRate(selectedDetail.id, r)} size={22} />
                      <span className="text-sm text-[#7C8068]">{selectedDetail.rating > 0 ? `${selectedDetail.rating}/5` : "No ratings yet"} ({selectedDetail.rating_count})</span>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" onClick={() => setSelectedDetail(null)} className="flex-1 border-[#D2C2B3]">Close</Button>
                    {isOwn ? (
                      <Button onClick={() => { handleUnpublish(selectedDetail.id); setSelectedDetail(null); }} className="flex-1 bg-[#C25A3C] hover:bg-[#A84B32] text-white">Unpublish</Button>
                    ) : (
                      <Button onClick={() => { handleImport(selectedDetail.id); setSelectedDetail(null); }} className="flex-1 bg-[#55624F] hover:bg-[#424D3D] text-white" data-testid="detail-import-btn">
                        <DownloadSimple size={16} className="mr-2" />Import to My Templates
                      </Button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketplacePage;
